"use client";

import { useInspectorMode } from "@utils/ContentfulUtils";
import { transparentPixel } from "@utils/string";
import { useLocale } from "next-intl";
import Image from "next/image";
import Link from "next/link";
import { FC, useRef } from "react";
import SwiperCore from "swiper";
import "swiper/css";
import "swiper/css/navigation";
import { Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import Icon from "@/components/ui/Icon/Icon";

import "./SocialMediaPresence.css";
import { SocialPresenceData } from "./type";

SwiperCore.use([Navigation]);

const SocialMediaPresence: FC<SocialPresenceData> = ({ entry }) => {
  const swiperRef = useRef<SwiperCore>();
  const inspectorMode = useInspectorMode(entry.id);
  const locale = useLocale();
  const [language] = locale.split("-");
  const handleSlideChange = (swiper: SwiperCore) => {
    swiper.update();
  };

  const changeSlide = (dir: string) => {
    const slideList = swiperRef?.current?.slides;
    if (!slideList) return;

    const currentIndex = [...slideList].findIndex((el) => el.classList.contains("swiper-slide-active"));

    if (currentIndex !== -1) {
      slideList[currentIndex].classList.remove("swiper-slide-active");
    }

    let newIndex = dir === "next" ? currentIndex + 1 : currentIndex - 1;

    if (newIndex < 0) {
      newIndex = 0;
    } else if (newIndex >= slideList.length) {
      newIndex = slideList.length - 1;
    }

    slideList[newIndex].classList.add("swiper-slide-active");
  };

  return (
    <div className="w-full bg-gray-7 py-xxl md:flex md:flex-row md:py-4xl">
      <div className="w-22\/24 mx-auto md:w-auto md:flex md:flex-row items-center ">
        {/*   <div className="md:w-5\/24"></div> */}
        <div className="md:w-auto md:px-s">
          <div
            className={`accent-font accent-title accent-color font-primary uppercase font-[600] text-l md:text-xl tracking-widest text-center ${language === "ar" ? "md:text-right" : "md:text-left"}`}
            {...inspectorMode.getProps("title")}
          >
            {entry.title}
          </div>
          {entry.subtitle && (
            <div
              {...inspectorMode.getProps("subtitle")}
              className={`text-center font-secondary font-secondary-light text-m leading-6 py-s ${language === "ar" ? "md:text-right" : "md:text-left"} text-black`}
            >
              {entry.subtitle}
            </div>
          )}
          <div className="mt-m" {...inspectorMode.getProps("cta")}>
            <Link href={entry.cta.link.url} target="_blank">
              <Button
                size={ButtonSize.SMALL}
                customClass="mx-auto md:mx-0 block"
                theme={ButtonTheme.BLACK}
                type="button"
                variant={ButtonVariant.SECONDARY}
              >
                <span className="flex items-center gap-xs font-secondary font-normal text-[14px] lowercase">
                  {entry.cta.link.icon && <Icon name={entry.cta.link.icon} size="20" />}
                  {entry.cta.label}
                </span>
              </Button>
            </Link>
          </div>
        </div>

        <div className="w-full flex flex-col items-end mt-l  ">
          <div className="w-23\/24 md:w-fit md:flex">
            <div className="hidden md:flex mr-s md:flex-row md:items-center">
              <div className="sx-arrow ml-s">
                <Button
                  rounded
                  size={ButtonSize.MEDIUM}
                  customClass="!border-white shadow"
                  theme={ButtonTheme.WHITE}
                  type="button"
                  variant={ButtonVariant.PRIMARY}
                  onClick={() => changeSlide("prev")}
                >
                  <span className="flex items-center gap-xs">
                    <Icon name="arrow-left" size="20" />
                  </span>
                </Button>
              </div>
            </div>
            <div className="md:max-w-[640px]" {...inspectorMode.getProps("cards")}>
              <Swiper
                className="w-full h-[430px] md:h-[554px]"
                slidesPerView={1.2}
                breakpoints={{
                  1024: {
                    slidesPerView: "auto",
                    spaceBetween: 20,
                  },
                }}
                spaceBetween={12}
                onSlideChange={handleSlideChange}
                onSwiper={(swiper) => (swiperRef.current = swiper)}
              >
                {entry.cards.map((card, index) => (
                  <SwiperSlide className="cardSocialPresence" key={index}>
                    <div className="w-full h-full">
                      <Image
                        src={card.url || transparentPixel}
                        className="object-cover w-full h-full rounded-lg"
                        alt={card.title}
                        width="300"
                        height="400"
                      />
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
            <div className="hidden md:flex md:w-2\/24 md:flex-row md:items-center">
              <div className=" dx-arrow pl-s">
                <Button
                  rounded
                  size={ButtonSize.MEDIUM}
                  customClass="!border-white shadow"
                  theme={ButtonTheme.WHITE}
                  type="button"
                  variant={ButtonVariant.PRIMARY}
                  onClick={() => changeSlide("next")}
                >
                  <span className="flex items-center gap-xs">
                    <Icon name="arrow-right" size="20" />
                  </span>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default SocialMediaPresence;
